import React, { useEffect, useState } from "react";
import { getAiConversation, listAiConversations, sendAiMessage } from "../../api/ai";
import MessageInput from "./MessageInput";
import MessageList from "./MessageList";
import logger from "../../utils/logger.js";

function formatDateTime(value, locale = "en") {
  if (!value) return "";

  try {
    return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

export default function ChatBox({ lang = "en" }) {
  const isAr = lang === "ar";
  const locale = isAr ? "ar" : "en";

  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [activeConversation, setActiveConversation] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadInitialData() {
      try {
        setLoadingList(true);
        setError("");

        const items = await listAiConversations(20);
        if (!active) return;

        setConversations(items);

        if (items.length > 0) {
          const firstConversationId = Number(items[0].id);
          setActiveConversationId(firstConversationId);
          await loadConversation(firstConversationId, active);
        } else {
          setActiveConversation(null);
          setActiveConversationId(null);
        }
      } catch (err) {
        logger.error("AI conversations load error", err);
        if (active) {
          setError(isAr ? "تعذر تحميل محادثات المساعد." : "Unable to load assistant conversations.");
        }
      } finally {
        if (active) {
          setLoadingList(false);
        }
      }
    }

    loadInitialData();

    return () => {
      active = false;
    };
  }, [isAr]);

  async function loadConversation(conversationId, isActive = true) {
    try {
      setLoadingConversation(true);
      setError("");

      const conversation = await getAiConversation(conversationId);
      if (!isActive) return;

      setActiveConversation(conversation);
      setActiveConversationId(Number(conversation?.id || conversationId));
    } catch (err) {
      logger.error("AI conversation details error", err);
      if (isActive) {
        setError(isAr ? "تعذر تحميل تفاصيل المحادثة." : "Unable to load conversation details.");
      }
    } finally {
      if (isActive) {
        setLoadingConversation(false);
      }
    }
  }

  async function refreshConversationList(selectedConversationId = activeConversationId) {
    const items = await listAiConversations(20);
    setConversations(items);

    if (selectedConversationId) {
      const matched = items.find((item) => Number(item.id) === Number(selectedConversationId));
      if (matched) {
        setActiveConversationId(Number(matched.id));
      }
    }

    return items;
  }

  async function handleSelectConversation(conversationId) {
    if (!conversationId || Number(conversationId) === Number(activeConversationId) || sending) {
      return;
    }

    await loadConversation(Number(conversationId));
  }

  async function handleStartNewConversation() {
    if (sending) return;

    setActiveConversation(null);
    setActiveConversationId(null);
    setError("");
  }

  async function handleSendMessage(message) {
    try {
      setSending(true);
      setError("");

      const response = await sendAiMessage({
        message,
        locale,
        conversationId: activeConversationId,
      });

      const conversationId = Number(response?.conversation_id || activeConversationId || 0);

      if (!conversationId) {
        throw new Error("Missing conversation_id");
      }

      await refreshConversationList(conversationId);
      await loadConversation(conversationId);
    } catch (err) {
      logger.error("AI message send error", err);
      setError(isAr ? "تعذر إرسال الرسالة الآن." : "Unable to send your message right now.");
    } finally {
      setSending(false);
    }
  }

  const conversationMessages = Array.isArray(activeConversation?.messages)
    ? activeConversation.messages
    : [];

  return (
    <section className="ai-chatbox">
      <aside className="ai-chatbox__sidebar">
        <div className="ai-chatbox__sidebar-header">
          <div>
            <div className="ai-chatbox__eyebrow">{isAr ? "محادثاتك" : "Your conversations"}</div>
            <h2 className="ai-chatbox__sidebar-title">{isAr ? "المساعد الذكي" : "AI Assistant"}</h2>
          </div>

          <button
            type="button"
            className="ai-chatbox__new-button"
            onClick={handleStartNewConversation}
            disabled={sending}
          >
            {isAr ? "محادثة جديدة" : "New chat"}
          </button>
        </div>

        {loadingList ? (
          <div className="ai-chatbox__sidebar-state">
            {isAr ? "جارِ تحميل المحادثات..." : "Loading conversations..."}
          </div>
        ) : conversations.length === 0 ? (
          <div className="ai-chatbox__sidebar-state">
            {isAr ? "لا توجد محادثات بعد." : "No conversations yet."}
          </div>
        ) : (
          <div className="ai-chatbox__conversation-list">
            {conversations.map((conversation) => {
              const isActiveConversation = Number(conversation.id) === Number(activeConversationId);

              return (
                <button
                  key={conversation.id}
                  type="button"
                  className={`ai-chatbox__conversation-item ${
                    isActiveConversation ? "is-active" : ""
                  }`}
                  onClick={() => handleSelectConversation(conversation.id)}
                  disabled={sending}
                >
                  <div className="ai-chatbox__conversation-title">
                    {conversation.title || (isAr ? "محادثة بدون عنوان" : "Untitled conversation")}
                  </div>
                  <div className="ai-chatbox__conversation-preview">
                    {conversation?.latest_message?.content ||
                      (isAr ? "ابدأ بسؤال جديد" : "Start with a new question")}
                  </div>
                  <div className="ai-chatbox__conversation-meta">
                    <span>{conversation.current_agent || (isAr ? "عام" : "General")}</span>
                    <span>{formatDateTime(conversation.last_message_at, locale)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </aside>

      <div className="ai-chatbox__main">
        <header className="ai-chatbox__header">
          <div>
            <div className="ai-chatbox__eyebrow">
              {isAr ? "مساعد المستخدم" : "Customer assistant"}
            </div>
            <h1 className="ai-chatbox__title">
              {activeConversation?.title || (isAr ? "محادثة جديدة" : "New conversation")}
            </h1>
          </div>

          <div className="ai-chatbox__header-meta">
            {activeConversation?.current_agent ? (
              <span className="ai-chatbox__status-pill">
                {isAr ? "الوكيل" : "Agent"}: {activeConversation.current_agent}
              </span>
            ) : null}
            <span className="ai-chatbox__status-pill">
              {sending
                ? (isAr ? "جارِ المعالجة..." : "Processing...")
                : (isAr ? "جاهز" : "Ready")}
            </span>
          </div>
        </header>

        {error ? <div className="alert alert-danger rounded-4 mb-3">{error}</div> : null}

        <MessageList
          messages={conversationMessages}
          isAr={isAr}
          loading={loadingConversation}
        />

        <MessageInput
          isAr={isAr}
          onSend={handleSendMessage}
          loading={sending}
        />
      </div>
    </section>
  );
}
